import { useEffect, useState } from "react";
import { Mail, Trash2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { UserRepository } from "@/infrastructure/repositories/UserRepository";
import { NotificationParser, ParsedNotification } from "@/application/services/NotificationService";
import { storageService } from "@/application/services/StorageService";
import { LoggerFactory } from "@/application/logging/Logger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alertDialog";

interface ConfirmDialogData {
  action: "delete" | "clear" | "decision";
  item?: ParsedNotification;
  accept?: boolean;
}

interface ResultDialogData {
  success: boolean;
  message: string;
}

export function NotificationContent() {
  const userRepo = new UserRepository();
  const logger = LoggerFactory.getLogger();
  const [notifications, setNotifications] = useState<ParsedNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmData, setConfirmData] = useState<ConfirmDialogData | null>(null);
  const [resultDialog, setResultDialog] = useState<ResultDialogData | null>(null);

  const userId = Number(storageService.getItem("current_user_swimmer_id"));

  /** ---------------- LOAD NOTIFICATIONS ------------------ **/
  useEffect(() => {
    async function loadNotifications() {
      try {
        setLoading(true);
        const raw = await userRepo.fetchNotifications(userId);
        const safeRaw = Array.isArray(raw) ? raw : [];
        const parsed = safeRaw.map((item: any) => NotificationParser.parseNotification(item));
        setNotifications(parsed);
      } catch (error) {
        logger.error("Failed to load notifications", error as Error, { userId });
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    }

    if (userId) {
      loadNotifications();
    }
  }, [userId]);

  /** ---------------- HANDLE DECISION (ACCEPT/DECLINE) ------------------ **/
  async function runDecision(notification: ParsedNotification, accept: boolean) {
    try {
      let result = 0;

      switch (notification.action) {
        case 3:
          result = await userRepo.acceptCoachConnectionFromSwimmer(0, Number(notification.paramId), accept);
          break;
        case 1:
          result = await userRepo.acceptSwimmerConnection(userId, Number(notification.paramId), accept);
          break;
        case 2:
          result = await userRepo.acceptJoinTeam(Number(notification.paramId), Number(notification.paramValue), accept);
          break;
        case 4:
          result = await userRepo.acceptInviteTeam(userId, Number(notification.paramId), Number(notification.paramValue), accept);
          break;
        case 5:
          result = await userRepo.acceptCoachConnectionFromCoach(userId, Number(notification.paramId), accept);
          break;
      }

      if (result > 0) {
        await userRepo.deleteNotification(userId, notification.id);
        setNotifications(notifications.filter(x => x.id !== notification.id));
        setResultDialog({ success: true, message: "Action completed successfully!" });
      } else {
        setResultDialog({ success: false, message: "Something went wrong. Please try again." });
      }
    } catch (error) {
      logger.error("Failed to process notification decision", error as Error, { notificationId: notification.id });
      setResultDialog({ success: false, message: "An error occurred. Please try again." });
    }
  }

  /** ---------------- HANDLE DELETE ------------------ **/
  async function runDelete(notification: ParsedNotification) {
    try {
      const ok = await userRepo.deleteNotification(userId, notification.id);
      if (ok) {
        setNotifications(notifications.filter(x => x.id !== notification.id));
        setResultDialog({ success: true, message: "Notification deleted." });
      } else {
        setResultDialog({ success: false, message: "Failed to delete notification." });
      }
    } catch (error) {
      logger.error("Failed to delete notification", error as Error, { notificationId: notification.id });
      setResultDialog({ success: false, message: "An error occurred. Please try again." });
    }
  }

  /** ---------------- HANDLE CLEAR ALL ------------------ **/
  async function runClearAll() {
    try {
      await userRepo.clearNotifications(userId);
      setNotifications([]);
      setResultDialog({ success: true, message: "All notifications cleared." });
    } catch (error) {
      logger.error("Failed to clear notifications", error as Error, { userId });
      setResultDialog({ success: false, message: "Failed to clear notifications." });
    }
  }

  /** ------------------------------ UI ------------------------------ **/
  const unreadCount = notifications.filter(n => n.status === 0).length;

  console.log(unreadCount);
  
  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Notification</h1>
          <p className="text-muted-foreground">Your messages and notifications</p>
        </div>

        <div className="flex items-center space-x-2">
          {notifications.length > 0 && (
            <Button variant="destructive" onClick={() => setConfirmData({ action: "clear" })}>
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* LOADING */}
      {loading && (
       <div className="fixed inset-0 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
        )}

      {!loading && (
        <>
          {/* LIST */}
          <div className="space-y-2">
            {notifications.map((msg) => (
              <Card
                key={msg.id}
                className={`hover:shadow-md transition-shadow ${
                  msg.status === 0 ? "border-l-4 border-l-blue-500" : ""
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start space-x-4">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src="/placeholder-avatar.jpg" />
                      <AvatarFallback>{msg.actionData?.[0]}</AvatarFallback>
                    </Avatar>

                    <div className="flex-1">
                      <p className="font-medium">{NotificationParser.getMessage(msg)}</p>

                      {/* ACCEPT / DECLINE */}
                      {msg.status === 0 && (
                        <div className="flex gap-3 mt-3">
                          <Button size="sm" onClick={() => setConfirmData({ action: "decision", item: msg, accept: true })}>
                            Accept
                          </Button>

                          <Button size="sm" variant="destructive" onClick={() => setConfirmData({ action: "decision", item: msg, accept: false })}>
                            Decline
                          </Button>
                        </div>
                      )}

                      {msg.status !== 0 && (
                        <p className={`mt-2 text-sm ${msg.status === 1 ? "text-green-600" : "text-red-600"}`}>
                          {msg.status === 1 ? "✔ Accepted" : "✘ Declined"}
                        </p>
                      )}
                    </div>

                    {/* DELETE */}
                    <Button variant="ghost" size="icon" onClick={() => setConfirmData({ action: "delete", item: msg })}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* EMPTY */}
          {notifications.length === 0 && !loading && (
            <Card>
              <CardContent className="p-12 text-center">
                <Mail className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No notifications</h3>
                <p className="text-muted-foreground">You don't have any notifications yet.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* CONFIRMATION DIALOG */}
      {confirmData && (
        <AlertDialog open={true}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmData.action === "delete" && "Do you want to delete this notification?"}
                {confirmData.action === "clear" && "Do you want to clear all notifications?"}
                {confirmData.action === "decision" &&
                  `Are you sure you want to ${confirmData.accept ? "ACCEPT" : "DECLINE"} this request?`}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmData(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  const data = confirmData;
                  setConfirmData(null);

                  if (data.action === "delete" && data.item) await runDelete(data.item);
                  if (data.action === "clear") await runClearAll();
                  if (data.action === "decision" && data.item) await runDecision(data.item, data.accept ?? false);
                }}
              >
                Yes
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* RESULT DIALOG */}
      {resultDialog && (
        <AlertDialog open={true}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {resultDialog.success ? "Success" : "Failed"}
              </AlertDialogTitle>
              <AlertDialogDescription>{resultDialog.message}</AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setResultDialog(null)}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
